import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function StudentApprovals() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId } = useBranchFilter();
  const [pendingStudents, setPendingStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [receptionists, setReceptionists] = useState({}); // Map of receptionist_id to receptionist data
  const [classes, setClasses] = useState([]); // Array of {id, class_name}
  const [classesList, setClassesList] = useState([]); // Array of classes for dropdown
  const [sections, setSections] = useState([]); // Array of sections
  const [loadingSections, setLoadingSections] = useState(false);
  const [grades, setGrades] = useState([]); // Array of {id, name}
  const [streams, setStreams] = useState([]); // Array of {id, name, grade_id}
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [loadingStreams, setLoadingStreams] = useState(false);
  const [approvingStudent, setApprovingStudent] = useState(null); // Student being approved
  const [studentIdNo, setStudentIdNo] = useState(''); // Student ID No. input
  const [classId, setClassId] = useState(''); // Selected class ID
  const [sectionId, setSectionId] = useState(''); // Selected section ID
  const [gradeId, setGradeId] = useState(''); // Selected grade ID
  const [streamId, setStreamId] = useState(''); // Selected stream ID
  const [notification, setNotification] = useState(null); // Success/Error notification
  const [showRejectConfirmPopup, setShowRejectConfirmPopup] = useState(false);
  const [studentToReject, setStudentToReject] = useState(null);
  const [showRejectSuccessPopup, setShowRejectSuccessPopup] = useState(false);
  const [rejectedStudentName, setRejectedStudentName] = useState('');
  const [showApproveSuccessPopup, setShowApproveSuccessPopup] = useState(false);
  const [approvedStudentName, setApprovedStudentName] = useState('');
  
  // Transport fields for approval
  const [pickupLocations, setPickupLocations] = useState([]);
  const [loadingPickupLocations, setLoadingPickupLocations] = useState(false);
  const [approvalPickupLocationId, setApprovalPickupLocationId] = useState('');
  const [approvalTransportRouteType, setApprovalTransportRouteType] = useState('');
  const [approvalTransportTime, setApprovalTransportTime] = useState('');
  const [approvalRouteId, setApprovalRouteId] = useState('');
  const [availableRoutes, setAvailableRoutes] = useState([]);

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchPendingStudents();
      fetchReceptionists();
      fetchClasses();
      fetchGrades();
      fetchPickupLocations();
      fetchAllRoutes(); // Fetch all routes and vehicles
    }
  }, [schoolData?.id, selectedBranchId]);

  const fetchPendingStudents = async () => {
    if (!schoolData?.id) return;
    
    try {
      setLoadingStudents(true);
      
      // Fetch students that are not approved (added by receptionists)
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_approved', false) // Only show pending approvals
        .order('created_at', { ascending: false });
      
      if (studentsError) {
        console.error('Error fetching pending students:', studentsError);
        setError('Failed to load pending students');
        return;
      }
      
      // Fetch classes for class names
      const { data: classesData, error: classesError } = await supabase
        .from('classes')
        .select('id, class_name')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);
      
      const classMap = {};
      if (!classesError && classesData) {
        classesData.forEach(cls => {
          classMap[cls.id] = cls.class_name;
        });
        setClasses(classMap);
      }
      
      // Combine students with their class names
      const studentsWithClasses = (studentsData || []).map(student => ({
        ...student,
        class_name: classMap[student.class_id] || 'No Class'
      }));
      
      setPendingStudents(studentsWithClasses);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load pending students');
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchReceptionists = async () => {
    if (!schoolData?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('receptionists')
        .select('id, name, email')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);
      
      if (!error && data) {
        const receptionistMap = {};
        data.forEach(rec => {
          receptionistMap[rec.id] = rec;
        });
        setReceptionists(receptionistMap);
      }
    } catch (err) {
      console.error('Error fetching receptionists:', err);
    }
  };

  // Helper function to extract base class name (without section)
  const getClassDisplayName = (className) => {
    if (!className) return '';
    // Extract only the class name part (before " - ") if section name is included
    const baseName = className.includes(' - ') 
      ? className.split(' - ')[0].trim() 
      : className.trim();
    // Normalize: remove extra spaces and ensure consistent format
    return baseName.replace(/\s+/g, ' ').trim();
  };

  const fetchClasses = async () => {
    if (!schoolData?.id) return;
    
    try {
      let query = supabase
        .from('classes')
        .select('id, class_name')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;
      
      if (!error && data) {
        const classMap = {};
        data.forEach(cls => {
          classMap[cls.id] = cls.class_name;
        });
        setClasses(classMap);
        
        // Deduplicate classes by base class name - only keep one class per base name
        const uniqueClassesMap = new Map();
        data.forEach(cls => {
          const baseClassName = getClassDisplayName(cls.class_name);
          // Normalize the key to ensure case-insensitive and whitespace-insensitive matching
          const normalizedKey = baseClassName.toLowerCase().trim();
          
          // Only add if we haven't seen this base class name before (case-insensitive)
          if (!uniqueClassesMap.has(normalizedKey)) {
            uniqueClassesMap.set(normalizedKey, {
              ...cls,
              displayName: baseClassName
            });
          }
        });
        
        // Convert map to array - each base class name appears only once
        const uniqueClasses = Array.from(uniqueClassesMap.values());
        
        // Sort by display name for better UX (Grade 1, Grade 2, etc.)
        uniqueClasses.sort((a, b) => {
          const nameA = a.displayName || '';
          const nameB = b.displayName || '';
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
        });
        
        setClassesList(uniqueClasses);
      }
    } catch (err) {
      console.error('Error fetching classes:', err);
    }
  };

  const fetchGrades = async () => {
    if (!schoolData?.id) return;
    
    try {
      setLoadingGrades(true);
      let gradesQuery = supabase
        .from('grades')
        .select('id, name')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        gradesQuery = gradesQuery.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await gradesQuery.order('name');
      
      if (!error && data) {
        setGrades(data);
      }
    } catch (err) {
      console.error('Error fetching grades:', err);
    } finally {
      setLoadingGrades(false);
    }
  };

  const fetchStreamsForGrade = async (selectedGradeId) => {
    if (!selectedGradeId || !schoolData?.id) {
      setStreams([]);
      setStreamId('');
      return;
    }

    try {
      setLoadingStreams(true);
      let streamsQuery = supabase
        .from('streams')
        .select('id, name, grade_id')
        .eq('grade_id', selectedGradeId)
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        streamsQuery = streamsQuery.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await streamsQuery.order('name');

      if (!error && data) {
        setStreams(data);
      } else {
        setStreams([]);
      }
    } catch (err) {
      console.error('Error fetching streams:', err);
      setStreams([]);
    } finally {
      setLoadingStreams(false);
    }
  };

  const fetchSectionsForClass = async (selectedClassId) => {
    if (!selectedClassId || !schoolData?.id) {
      setSections([]);
      setSectionId('');
      return;
    }

    try {
      setLoadingSections(true);
      
      // First, get the selected class to find its base class name
      let selectedClassQuery = supabase
        .from('classes')
        .select('id, class_name, sections')
        .eq('id', selectedClassId)
        .eq('school_id', schoolData.id);

      if (selectedBranchId != null) {
        selectedClassQuery = selectedClassQuery.eq('branch_id', selectedBranchId);
      }

      const { data: selectedClassData, error: selectedClassError } = await selectedClassQuery.maybeSingle();

      if (selectedClassError || !selectedClassData) {
        console.error('Error fetching selected class:', selectedClassError);
        setSections([]);
        setLoadingSections(false);
        return;
      }

      // Get the base class name (e.g., "Grade 2" from "Grade 2 - Starfish")
      const baseClassName = getClassDisplayName(selectedClassData.class_name);

      // Now find ALL classes with the same base class name to get all sections
      let allClassesQuery = supabase
        .from('classes')
        .select('id, class_name, sections')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId != null) {
        allClassesQuery = allClassesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: allClassesData, error: allClassesError } = await allClassesQuery;

      if (allClassesError) {
        console.error('Error fetching all classes:', allClassesError);
        setSections([]);
        setLoadingSections(false);
        return;
      }

      // Filter classes that have the same base class name
      const matchingClasses = (allClassesData || []).filter(cls => {
        const clsBaseName = getClassDisplayName(cls.class_name);
        return clsBaseName === baseClassName;
      });

      // Collect all sections from all matching classes
      const allSections = new Set();
      matchingClasses.forEach(cls => {
        if (cls.sections && Array.isArray(cls.sections)) {
          cls.sections.forEach(section => {
            if (section && section.trim() !== '') {
              allSections.add(section.trim());
            }
          });
        }
      });

      // Convert Set to sorted array
      const sectionsArray = Array.from(allSections).sort();

      setSections(sectionsArray);
    } catch (err) {
      console.error('Error fetching sections:', err);
      setSections([]);
    } finally {
      setLoadingSections(false);
    }
  };

  const fetchAllRoutes = async () => {
    if (!schoolData?.id) {
      console.log('No school data available');
      return;
    }
    
    try {
      console.log('Fetching all routes for school:', schoolData.id);
      // Fetch all routes for this school
      const { data: routesData, error: routesError } = await supabase
        .from('routes')
        .select('id, route_type, route_name, vehicle_id')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('route_name');
      
      if (routesError) {
        console.error('Error fetching routes:', routesError);
        setAvailableRoutes([]);
        return;
      }
      
      console.log('Routes fetched:', routesData?.length || 0);
      
      if (routesData && routesData.length > 0) {
        // Fetch vehicles for each route
        const routesWithVehicles = await Promise.all(
          routesData.map(async (route) => {
            let vehicle = null;
            if (route.vehicle_id) {
              const { data: vehicleData, error: vehicleError } = await supabase
                .from('transport_vehicles')
                .select('id, bus_number, driver_name')
                .eq('id', route.vehicle_id)
                .single();
              
              if (!vehicleError && vehicleData) {
                vehicle = vehicleData;
              }
            }
            
            return {
              id: route.id,
              route_type: route.route_type,
              route_name: route.route_name,
              vehicle: vehicle
            };
          })
        );
        
        console.log('Routes with vehicles:', routesWithVehicles.length);
        // Set all routes as available routes
        setAvailableRoutes(routesWithVehicles);
      } else {
        console.log('No routes found');
        setAvailableRoutes([]);
      }
    } catch (err) {
      console.error('Error fetching all routes:', err);
      setAvailableRoutes([]);
    }
  };

  const fetchPickupLocations = async () => {
    if (!schoolData?.id) return;
    
    try {
      setLoadingPickupLocations(true);
      const { data: locationsData, error: locationsError } = await supabase
        .from('pickup_locations')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('location_name');
      
      if (locationsError) {
        console.error('Error fetching pickup locations:', locationsError);
        setPickupLocations([]);
        return;
      }
      
      // Fetch routes for each location
      const locationsWithRoutes = await Promise.all(
        (locationsData || []).map(async (location) => {
          const { data: routeLocationData } = await supabase
            .from('route_pickup_locations')
            .select('route_id')
            .eq('pickup_location_id', location.id);
          
          let routes = [];
          if (routeLocationData && routeLocationData.length > 0) {
            const routeIds = routeLocationData.map(rl => rl.route_id);
            
            const { data: routesData } = await supabase
              .from('routes')
              .select('id, route_type, route_name, vehicle_id')
              .in('id', routeIds)
              .eq('is_active', true);
            
            if (routesData && routesData.length > 0) {
              const routesWithVehicles = await Promise.all(
                routesData.map(async (route) => {
                  const { data: vehicleData } = await supabase
                    .from('transport_vehicles')
                    .select('id, bus_number, driver_name')
                    .eq('id', route.vehicle_id)
                    .single();
                  
                  return {
                    id: route.id,
                    route_type: route.route_type,
                    route_name: route.route_name,
                    vehicle: vehicleData || null
                  };
                })
              );
              
              routes = routesWithVehicles;
            }
          }
          
          return {
            ...location,
            routes: routes
          };
        })
      );
      
      setPickupLocations(locationsWithRoutes);
    } catch (err) {
      console.error('Error fetching pickup locations:', err);
      setPickupLocations([]);
    } finally {
      setLoadingPickupLocations(false);
    }
  };

  const handleApproveClick = async (student) => {
    setApprovingStudent(student);
    setStudentIdNo(student.student_id_no || '');
    setClassId(student.class_id || '');
    setSectionId(student.section_id || '');
    
    // If student has a class_id, fetch sections for that class
    if (student.class_id) {
      fetchSectionsForClass(student.class_id);
    }
    
    // Pre-fill transport fields if student has transport enabled
    setApprovalPickupLocationId(student.pickup_location_id || '');
    setApprovalTransportRouteType(student.transport_route_type || '');
    setApprovalTransportTime(student.transport_time || '');
    setApprovalRouteId(student.route_id || '');
    // Update available routes based on pickup location
    if (student.pickup_location_id) {
      // Show all routes when pickup location is selected
      fetchAllRoutes();
    }
  };

  const updateAvailableRoutes = (locationId) => {
    // When a pickup location is selected, show ALL routes
    if (locationId) {
      fetchAllRoutes();
    } else {
      setAvailableRoutes([]);
    }
  };

  const closeApprovePopup = () => {
    setApprovingStudent(null);
    setStudentIdNo('');
    setClassId('');
    setSectionId('');
    setSections([]);
    setApprovalPickupLocationId('');
    setApprovalTransportRouteType('');
    setApprovalTransportTime('');
    setApprovalRouteId('');
    setAvailableRoutes([]);
  };

  const handleGradeChange = async (e) => {
    const selectedGradeId = e.target.value;
    setGradeId(selectedGradeId);
    setStreamId(''); // Reset stream when grade changes
    setClassId(''); // Reset class when grade changes
    await fetchStreamsForGrade(selectedGradeId);
  };

  const handleStreamChange = async (e) => {
    const selectedStreamId = e.target.value;
    setStreamId(selectedStreamId);
    
    // Find the class that matches this grade and stream
    if (gradeId && selectedStreamId) {
      try {
        let classQuery = supabase
          .from('classes')
          .select('id')
          .eq('school_id', schoolData.id)
          .eq('grade_id', gradeId)
          .eq('stream_id', selectedStreamId)
          .eq('is_active', true);

        // Filter by branch_id if selected
        if (selectedBranchId) {
          classQuery = classQuery.eq('branch_id', selectedBranchId);
        }

        const { data: classData, error } = await classQuery.maybeSingle();
        
        if (!error && classData) {
          setClassId(classData.id.toString());
          // Fetch sections for this class
          fetchSectionsForClass(classData.id);
        } else {
          setClassId('');
          setSections([]);
        }
      } catch (err) {
        console.error('Error finding class for grade and stream:', err);
        setClassId('');
        setSections([]);
      }
    } else {
      setClassId('');
      setSections([]);
    }
  };

  const handleApprove = async () => {
    if (!approvingStudent) return;

    // Validate that class is selected
    if (!classId) {
      setNotification({ type: 'error', message: 'Please select a Class Name' });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    const finalClassId = classId;

    try {
      setIsProcessing(true);
      
      const updateData = {
        is_approved: true,
        updated_at: new Date().toISOString()
      };

      // Add student ID if provided
      if (studentIdNo.trim()) {
        updateData.student_id_no = studentIdNo.trim();
      }

      // Add class_id if available
      if (finalClassId) {
        updateData.class_id = finalClassId;
      }

      // Add section_id if provided
      if (sectionId) {
        updateData.section_id = sectionId;
      }

      // Add transport fields if transport is enabled
      if (approvingStudent.transport_service_enabled) {
        if (approvalPickupLocationId) {
          updateData.pickup_location_id = parseInt(approvalPickupLocationId, 10);
        }
        if (approvalTransportRouteType) {
          updateData.transport_route_type = approvalTransportRouteType;
        }
        if (approvalTransportTime) {
          updateData.transport_time = approvalTransportTime;
        }
        if (approvalRouteId) {
          updateData.route_id = parseInt(approvalRouteId, 10);
        }
        
        // Calculate transport price if pickup location and route type are set
        if (approvalPickupLocationId && approvalTransportRouteType) {
          const selectedLocation = pickupLocations.find(loc => loc.id.toString() === approvalPickupLocationId.toString());
          if (selectedLocation) {
            if (approvalTransportRouteType === '1 Way' && selectedLocation.price_one_way) {
              updateData.transport_price = parseFloat(selectedLocation.price_one_way);
            } else if (approvalTransportRouteType === '2 Way' && selectedLocation.price_two_way) {
              updateData.transport_price = parseFloat(selectedLocation.price_two_way);
            }
          }
        }
      }
      
      const { error } = await supabase
        .from('students')
        .update(updateData)
        .eq('id', approvingStudent.id);
      
      if (error) {
        console.error('Error approving student:', error);
        setNotification({ type: 'error', message: 'Failed to approve student. Please try again.' });
        setTimeout(() => setNotification(null), 5000);
        return;
      }
      
      // Store approved student name for success popup
      const approvedName = `${approvingStudent.first_name} ${approvingStudent.middle_name ? approvingStudent.middle_name + ' ' : ''}${approvingStudent.last_name}`;
      
      // Refresh the list
      await fetchPendingStudents();
      
      // Close popup
      closeApprovePopup();
      
      // Show success popup
      setApprovedStudentName(approvedName);
      setShowApproveSuccessPopup(true);
      
    } catch (err) {
      console.error('Error:', err);
      setNotification({ type: 'error', message: 'Failed to approve student. Please try again.' });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = (student) => {
    setStudentToReject(student);
    setShowRejectConfirmPopup(true);
  };

  const confirmReject = async () => {
    if (!studentToReject) return;

    try {
      setIsProcessing(true);
      
      // Delete the student record
      const { error } = await supabase
        .from('students')
        .delete()
        .eq('id', studentToReject.id);
      
      if (error) {
        console.error('Error rejecting student:', error);
        setNotification({ type: 'error', message: 'Failed to reject student. Please try again.' });
        setTimeout(() => setNotification(null), 5000);
        setIsProcessing(false);
        setShowRejectConfirmPopup(false);
        setStudentToReject(null);
        return;
      }
      
      // Store rejected student name for success popup
      const rejectedName = `${studentToReject.first_name} ${studentToReject.last_name}`;
      
      // Refresh the list
      await fetchPendingStudents();
      
      // Close confirmation popup
      setShowRejectConfirmPopup(false);
      setStudentToReject(null);
      
      // Show success popup
      setRejectedStudentName(rejectedName);
      setShowRejectSuccessPopup(true);
      
    } catch (err) {
      console.error('Error:', err);
      setNotification({ type: 'error', message: 'Failed to reject student. Please try again.' });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const cancelReject = () => {
    setShowRejectConfirmPopup(false);
    setStudentToReject(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">⏳</div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">❌</div>
        <p>School data not found. Please login again.</p>
        <button onClick={() => navigate('/schoollogin')} className="submit-btn">
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content">
        <div className="students-content">
          <div className="students-header">
            <h1>Student Approvals</h1>
            <p>Review and approve students added by receptionists</p>
          </div>

          <div className="students-stats">
            <div className="stat-item">
              <span className="stat-number">{pendingStudents.length}</span>
              <span className="stat-label">Pending Approvals</span>
            </div>
          </div>

          {loadingStudents ? (
            <div className="loading-container">
              <div className="loading-spinner">⏳</div>
              <p>Loading pending students...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">❌</div>
              <p>{error}</p>
              <button 
                className="retry-btn"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : pendingStudents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">✅</div>
              <h3>No Pending Approvals</h3>
              <p>All students have been approved. There are no pending approval requests.</p>
            </div>
          ) : (
            <div className="students-table-container">
              <table className="students-table" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width: '15%' }}>Student Name</th>
                    <th style={{ width: '15%' }}>Email</th>
                    <th style={{ width: '10%' }}>Class</th>
                    <th style={{ width: '10%' }}>Section</th>
                    <th style={{ width: '15%' }}>Added By</th>
                    <th style={{ width: '10%' }}>Date Added</th>
                    <th style={{ width: '25%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingStudents.map((student) => {
                    const receptionist = receptionists[student.created_by_receptionist_id];
                    const receptionistName = receptionist ? receptionist.name : 'Unknown';
                    
                    return (
                      <tr key={student.id}>
                        <td className="student-name" style={{ color: '#000000' }}>
                          <strong>{student.first_name} {student.middle_name ? student.middle_name + ' ' : ''}{student.last_name}</strong>
                        </td>
                        <td className="email-cell">
                          <a href={`mailto:${student.email}`} className="email-link">
                            {student.email}
                          </a>
                        </td>
                        <td style={{ color: '#000000' }}>
                          {student.class_name || 'No Class'}
                        </td>
                        <td style={{ color: '#000000' }}>
                          {student.section_id || 'N/A'}
                        </td>
                        <td style={{ color: '#000000' }}>
                          {receptionistName}
                        </td>
                        <td className="created-date">
                          {formatDate(student.created_at)}
                        </td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button 
                              className="approve-btn"
                              title="Approve Student"
                              onClick={() => handleApproveClick(student)}
                              disabled={isProcessing}
                              style={{
                                background: '#10B981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '0.5rem 1rem',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                marginRight: '0.5rem',
                                opacity: isProcessing ? 0.5 : 1
                              }}
                            >
                              ✓ Approve
                            </button>
                            <button 
                              className="reject-btn"
                              title="Reject Student"
                              onClick={() => handleReject(student)}
                              disabled={isProcessing}
                              style={{
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '0.5rem 1rem',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                opacity: isProcessing ? 0.5 : 1
                              }}
                            >
                              ✕ Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Approve Student Popup */}
      {approvingStudent && (
        <div className="popup-overlay" onClick={closeApprovePopup}>
            <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="popup-header" style={{ backgroundColor: '#10B981', borderRadius: '8px 8px 0 0', padding: '1rem 1.5rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                Approve Student
              </h3>
              <button className="close-btn" onClick={closeApprovePopup} style={{ color: '#FFFFFF', fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
            </div>
            
            <div style={{ padding: '1.5rem', backgroundColor: '#F9FAFB' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ margin: '0 0 1rem 0', color: '#374151', fontSize: '0.95rem' }}>
                  You are about to approve <strong>{approvingStudent.first_name} {approvingStudent.middle_name ? approvingStudent.middle_name + ' ' : ''}{approvingStudent.last_name}</strong>. 
                  They will be added to the students list.
                </p>
                
                <div className="form-group" style={{ marginTop: '1.5rem' }}>
                  <label htmlFor="classId" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    Class Name *
                  </label>
                  <select
                    id="classId"
                    value={classId}
                    onChange={(e) => {
                      setClassId(e.target.value);
                      if (e.target.value) {
                        fetchSectionsForClass(e.target.value);
                      } else {
                        setSections([]);
                        setSectionId('');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '0.95rem',
                      boxSizing: 'border-box',
                      backgroundColor: '#FFFFFF',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select Class Name</option>
                    {classesList.map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.displayName || getClassDisplayName(cls.class_name)}
                      </option>
                    ))}
                  </select>
                </div>

                {classId && (
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label htmlFor="sectionId" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                      Section *
                    </label>
                    <select
                      id="sectionId"
                      value={sectionId}
                      onChange={(e) => setSectionId(e.target.value)}
                      disabled={!classId || loadingSections}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '0.95rem',
                        boxSizing: 'border-box',
                        backgroundColor: !classId || loadingSections ? '#F3F4F6' : '#FFFFFF',
                        cursor: !classId || loadingSections ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value="">
                        {!classId 
                          ? 'Select Grade and Stream First' 
                          : loadingSections 
                            ? 'Loading sections...' 
                            : 'Select Section'}
                      </option>
                      {sections.map((section, index) => (
                        <option key={index} value={section}>
                          {section}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label htmlFor="studentIdNo" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    Student ID No. (Optional)
                  </label>
                  <input
                    type="text"
                    id="studentIdNo"
                    value={studentIdNo}
                    onChange={(e) => setStudentIdNo(e.target.value)}
                    placeholder="Enter Student ID No. (e.g., STU-2024-001)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '0.95rem',
                      boxSizing: 'border-box'
                    }}
                  />
                  <p style={{ margin: '0.5rem 0 0 0', color: '#6B7280', fontSize: '0.85rem' }}>
                    You can assign a Student ID now or leave it blank to assign later.
                  </p>
                </div>

                {/* Transport Service Fields - Only show if transport is enabled */}
                {approvingStudent.transport_service_enabled && (
                  <>
                    <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#EFF6FF', borderRadius: '8px', border: '1px solid #BFDBFE' }}>
                      <h4 style={{ margin: '0 0 0.75rem 0', color: '#1E40AF', fontSize: '1rem', fontWeight: '600' }}>
                        Transport Service Assignment
                      </h4>
                      
                      <div className="form-group" style={{ marginTop: '1rem' }}>
                        <label htmlFor="approvalPickupLocationId" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                          Select Pickup Location *
                        </label>
                        {loadingPickupLocations ? (
                          <div style={{ padding: '0.5rem', color: '#6B7280', fontSize: '0.9rem' }}>Loading pickup locations...</div>
                        ) : (
                          <select
                            id="approvalPickupLocationId"
                            value={approvalPickupLocationId}
                            onChange={(e) => {
                              setApprovalPickupLocationId(e.target.value);
                              // When pickup location is selected, load all routes
                              if (e.target.value) {
                                fetchAllRoutes();
                              } else {
                                setAvailableRoutes([]);
                              }
                              setApprovalRouteId(''); // Reset route when location changes
                            }}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: '0.95rem',
                              boxSizing: 'border-box',
                              backgroundColor: '#FFFFFF'
                            }}
                          >
                            <option value="">-- Select pickup location --</option>
                            {pickupLocations.map((location) => (
                              <option key={location.id} value={location.id}>
                                {location.location_name} - {location.address}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div className="form-group" style={{ marginTop: '1rem' }}>
                        <label htmlFor="approvalTransportRouteType" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                          Select Route Type *
                        </label>
                        <select
                          id="approvalTransportRouteType"
                          value={approvalTransportRouteType}
                          onChange={(e) => {
                            setApprovalTransportRouteType(e.target.value);
                            setApprovalTransportTime(e.target.value !== '1 Way' ? '' : approvalTransportTime);
                            setApprovalRouteId(''); // Reset route when route type changes
                            // Ensure routes are loaded if pickup location is selected
                            if (approvalPickupLocationId && availableRoutes.length === 0) {
                              fetchAllRoutes();
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '0.95rem',
                            boxSizing: 'border-box',
                            backgroundColor: '#FFFFFF'
                          }}
                        >
                          <option value="">-- Select route type --</option>
                          <option value="1 Way">1 Way</option>
                          <option value="2 Way">2 Way</option>
                        </select>
                      </div>

                      {/* Morning/Evening Selection for 1 Way */}
                      {approvalTransportRouteType === '1 Way' && (
                        <div className="form-group" style={{ marginTop: '1rem' }}>
                          <label htmlFor="approvalTransportTime" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                            Select Time *
                          </label>
                          <select
                            id="approvalTransportTime"
                            value={approvalTransportTime}
                            onChange={(e) => setApprovalTransportTime(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: '0.95rem',
                              boxSizing: 'border-box',
                              backgroundColor: '#FFFFFF'
                            }}
                          >
                            <option value="">-- Select time --</option>
                            <option value="Morning">Morning</option>
                            <option value="Evening">Evening</option>
                          </select>
                        </div>
                      )}

                      <div className="form-group" style={{ marginTop: '1rem' }}>
                        <label htmlFor="approvalRouteId" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                          Select Route & Vehicle (Optional)
                        </label>
                        <select
                          id="approvalRouteId"
                          value={approvalRouteId}
                          onChange={(e) => setApprovalRouteId(e.target.value)}
                          disabled={!approvalPickupLocationId}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '0.95rem',
                            boxSizing: 'border-box',
                            backgroundColor: !approvalPickupLocationId ? '#F3F4F6' : '#FFFFFF',
                            cursor: !approvalPickupLocationId ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <option value="">-- Select route (Optional) --</option>
                          {availableRoutes.map((route) => (
                            <option key={route.id} value={route.id}>
                              {route.route_name || `Route #${route.id}`} - {route.route_type}
                              {route.vehicle && ` (Vehicle: ${route.vehicle.bus_number})`}
                              {route.vehicle?.driver_name && ` - Driver: ${route.vehicle.driver_name}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={closeApprovePopup}
                  disabled={isProcessing}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    opacity: isProcessing ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={isProcessing}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    opacity: isProcessing ? 0.5 : 1
                  }}
                >
                  {isProcessing ? 'Approving...' : 'Approve Student'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Notification */}
      {notification && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            minWidth: '300px',
            maxWidth: '500px',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            backgroundColor: notification.type === 'success' ? '#10B981' : '#EF4444',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            animation: 'slideInRight 0.3s ease-out',
            borderLeft: `4px solid ${notification.type === 'success' ? '#059669' : '#DC2626'}`,
          }}
        >
          <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>
            {notification.type === 'success' ? '✓' : '✕'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>
              {notification.type === 'success' ? 'Success!' : 'Error!'}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: 0.95 }}>
              {notification.message}
            </div>
          </div>
          <button
            onClick={() => setNotification(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#FFFFFF',
              fontSize: '1.25rem',
              cursor: 'pointer',
              padding: '0',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
          >
            ×
          </button>
        </div>
      )}

      {/* Reject Confirmation Popup */}
      {showRejectConfirmPopup && studentToReject && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelReject}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✕</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Reject Student?
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              Are you sure you want to reject <strong>{studentToReject.first_name} {studentToReject.last_name}</strong>?
              <br /><br />
              This action cannot be undone and the student will be permanently removed from the system.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
              <button onClick={cancelReject} disabled={isProcessing} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: '2px solid #E5E7EB', background: '#FFFFFF', color: '#6B7280', fontSize: '1rem', fontWeight: '600', cursor: isProcessing ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isProcessing ? 0.5 : 1 }}>
                Cancel
              </button>
              <button onClick={confirmReject} disabled={isProcessing} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: isProcessing ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isProcessing ? 0.5 : 1, boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)' }}>
                {isProcessing ? 'Rejecting...' : 'Reject Student'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Success Popup */}
      {showRejectSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowRejectSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Student Rejected Successfully! ✅
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '2rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              <strong>{rejectedStudentName}</strong> has been rejected and removed from the system.
            </p>
            <button onClick={() => setShowRejectSuccessPopup(false)} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)' }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Approve Success Popup */}
      {showApproveSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowApproveSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Student Approved Successfully! 🎉
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '2rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              <strong>{approvedStudentName}</strong> has been approved and added to the students list.
            </p>
            <button onClick={() => setShowApproveSuccessPopup(false)} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
              OK
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default StudentApprovals;

